import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { generalConfigApi } from '../../../apis';
import {
  BatchUpsertWorkScheduleDto,
  CreateWorkScheduleDto,
  ExportWorkScheduleToCsvDto,
  WorkSchedule,
  WorkScheduleTypeEnum,
} from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import ImportCSV from '../../../components/ImportCSV/ImportCSV';
import ModalWrap from '../../../components/ModalWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import SvgArrowLeft from '../../../components/svg/SvgArrowLeft';
import SvgArrowRight from '../../../components/svg/SvgArrowRight';
import SvgCsv from '../../../components/svg/SvgCsv';
import SvgCsvGreen from '../../../components/svg/SvgCsvGreen';
import { QUERY_KEY } from '../../../constants/QueryKey';
import { DATE_FORMAT, YEAR_MONTH_DATE_FORMAT_JP } from '../../../constants/common';
import { downloadCSV } from '../../../utils';
import CalendarCustom from './components';
import './style.scss';

const WorkingHours = () => {
  const intl = useIntl();

  const [year, setYear] = useState<number>(dayjs().year());
  const [dataWorkingHour, setDataWorkingHour] = useState<WorkSchedule[]>([]);
  const [annualHolidays, setAnnualHolidays] = useState(0);
  const [clickedDays, setClickedDays] = useState<CreateWorkScheduleDto[]>([]);
  const [openModalCsv, setOpenModalCsv] = useState<boolean>(false);
  const [dataImportCSV, setDataImportCSV] = useState<CreateWorkScheduleDto[]>([]);
  const [dataCSVImportError, setDataCSVImportError] = useState<string>('');

  const handleNextYear = () => {
    setYear((prevYear) => prevYear + 1);
  };

  const handlePreviousYear = () => {
    setYear((prevYear) => prevYear - 1);
  };

  const upsertSchedule = useMutation({
    mutationFn: (batchUpsertWorkScheduleDto: BatchUpsertWorkScheduleDto) => {
      return generalConfigApi.generalConfigControllerBatchUpsertWorkSchedule(batchUpsertWorkScheduleDto);
    },
    onSuccess: () => {
      refetch();
      setClickedDays([]);
      ToastSuccess({
        content: intl.formatMessage({ id: 'workinghour.success' }),
      });

      if (openModalCsv) {
        handleCloseModalImportCSV();
      }
    },
    onError: () => {
      setClickedDays([]);
      ToastError({
        content: intl.formatMessage({ id: 'workinghour.failed' }),
      });
    },
  });

  const exportSchedule = useMutation({
    mutationFn: (exportWorkScheduleToCsvDto: ExportWorkScheduleToCsvDto) => {
      return generalConfigApi.generalConfigControllerExportWorkScheduleToCsv(exportWorkScheduleToCsvDto);
    },
    onSuccess: (data) => {
      downloadCSV(data?.data?.data, `working_schedule_${year}.csv`);
      ToastSuccess({
        content: intl.formatMessage({ id: 'common.csv.export.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'common.csv.export.failed' }),
      });
    },
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: [QUERY_KEY.WORKING_SCHEDULE, year],
    queryFn: () => generalConfigApi.generalConfigControllerGetWorkingSchedule(year),
    enabled: !!year,
  });

  const calculateInitialHolidays = (workingScheduleData) => {
    const weekendsAndHolidays = workingScheduleData?.reduce((acc, day) => {
      const isHoliday =
        day.type === WorkScheduleTypeEnum.RegulatedDaysOff || day.type === WorkScheduleTypeEnum.LegalDaysOff;
      if (isHoliday) {
        acc.push(day.date);
      }
      return acc;
    }, []);

    setAnnualHolidays(weekendsAndHolidays?.length);
  };

  const handleDayClick = (date, type) => {
    setClickedDays((prev) => {
      const updatedDays = [...prev];
      const existingDay = updatedDays.find((day) => day.date === date);
      const checkDate = dataWorkingHour.find((day) => day.date === date);

      if (checkDate) {
        if (type === WorkScheduleTypeEnum?.WorkingDays) {
          setAnnualHolidays((prevCount) => prevCount);
        } else if (type === WorkScheduleTypeEnum.LegalDaysOff) {
          setAnnualHolidays((prevCount) => prevCount + 1);
        } else {
          setAnnualHolidays((prevCount) => prevCount - 1);
        }

        if (checkDate.type === WorkScheduleTypeEnum.WorkingDays) {
          checkDate.type = WorkScheduleTypeEnum.RegulatedDaysOff;
        } else if (checkDate.type === WorkScheduleTypeEnum.RegulatedDaysOff) {
          checkDate.type = WorkScheduleTypeEnum.LegalDaysOff;
        } else {
          checkDate.type = WorkScheduleTypeEnum.WorkingDays;
        }
      }

      if (existingDay) {
        if (existingDay.type === WorkScheduleTypeEnum.WorkingDays) {
          existingDay.type = WorkScheduleTypeEnum.RegulatedDaysOff;
        } else if (existingDay.type === WorkScheduleTypeEnum.RegulatedDaysOff) {
          existingDay.type = WorkScheduleTypeEnum.LegalDaysOff;
        } else {
          existingDay.type = WorkScheduleTypeEnum.WorkingDays;
        }
      } else {
        updatedDays.push({
          date,
          type: checkDate ? checkDate.type : WorkScheduleTypeEnum.WorkingDays, // Ensure 'type' is defined directly
        });
      }

      return updatedDays;
    });
  };

  const handleExport = () => {
    if (data?.data?.data) {
      const exportData = {
        year,
      };
      exportSchedule.mutate(exportData);
    }
  };

  const handleSave = () => {
    const batchUpsertData = {
      data: clickedDays.map((workingSchedule) => ({
        ...workingSchedule,
        date: dayjs(workingSchedule.date, DATE_FORMAT).format(YEAR_MONTH_DATE_FORMAT_JP),
      })),
    };
    upsertSchedule.mutate(batchUpsertData);
  };

  const mapTypeValue = (type: string): CreateWorkScheduleDto['type'] => {
    if (type === '出勤日') return WorkScheduleTypeEnum?.WorkingDays;
    if (type === '法定休日') return WorkScheduleTypeEnum.LegalDaysOff;
    if (type === '所定休日') return WorkScheduleTypeEnum.RegulatedDaysOff;
    return WorkScheduleTypeEnum?.WorkingDays;
  };

  const handleSubmitImportCSV = () => {
    if (isEmpty(dataImportCSV)) {
      return ToastError({
        content: intl.formatMessage({ id: 'staff-management.import.empty' }),
      });
    }

    const payload = {
      data: dataImportCSV.map((item) => ({
        ...item,
        type: mapTypeValue(item.type),
        date: item.date,
      })),
    };

    if (payload.data.length > 0) {
      upsertSchedule.mutate(payload);
    }
  };
  const handleImportCSV = (e: any) => {
    setDataImportCSV(e);
  };

  useEffect(() => {
    if (data && data.data && Array.isArray(data.data.data)) {
      setDataWorkingHour(data.data.data);
      calculateInitialHolidays(data.data.data);
    } else {
      setDataWorkingHour([]);
    }
  }, [data]);

  const handleConvertDataFile = (fileData: unknown[], keyMapping?: Object) => {
    return fileData
      .map((item) => {
        const workSchedule: CreateWorkScheduleDto = { date: '', type: 'WORKING_DAYS' };

        for (const key in item as Record<string | number, unknown>) {
          const attr = keyMapping?.[key] || key;
          workSchedule[attr] = item?.[key] || '';

          if (item?.[key]) {
            if (attr === 'date') {
              workSchedule.date = dayjs(item?.[key], YEAR_MONTH_DATE_FORMAT_JP, true).isValid()
                ? dayjs(item?.[key], YEAR_MONTH_DATE_FORMAT_JP).format(YEAR_MONTH_DATE_FORMAT_JP)
                : '';
            }
          }
        }

        return workSchedule;
      })
      .filter((workSchedule) => workSchedule.date);
  };

  const handleCloseModalImportCSV = () => {
    setDataCSVImportError('');
    setOpenModalCsv(false);
    setDataImportCSV([]);
  };

  return (
    <div className="working-hours">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'adminMenu.workingHours' })]}
        content={
          <>
            <div className="working-hours__top">
              <div className="working-hours__top-left">
                <div className="working-hours__top-title">
                  {year} {intl.formatMessage({ id: 'workinghour.year' })}
                </div>
                <div className="working-hours__top-arrow">
                  <div onClick={handlePreviousYear} className="working-hours__top-arrow-left">
                    <SvgArrowLeft />
                  </div>
                  <div onClick={handleNextYear} className="working-hours__top-arrow-right">
                    <SvgArrowRight />
                  </div>
                </div>
                <div className="working-hours__top-day-off">
                  {intl.formatMessage({ id: 'workinghour.annualHolidays' })}: {annualHolidays}
                </div>
                <div className="working-hours__top-color-text">
                  <div className="working-hours__top-color-text-one">
                    <div className="working-hours__top-color-text-one-color-1"></div>
                    <div className="working-hours__top-color-text-one-text">
                      {intl.formatMessage({ id: 'workinghour.workingDays' })}
                    </div>
                  </div>
                  <div className="working-hours__top-color-text-one">
                    <div className="working-hours__top-color-text-one-color-2"></div>
                    <div className="working-hours__top-color-text-one-text">
                      {intl.formatMessage({ id: 'workinghour.prescribedHolidays' })}
                    </div>
                  </div>
                  <div className="working-hours__top-color-text-one">
                    <div className="working-hours__top-color-text-one-color-3"></div>
                    <div className="working-hours__top-color-text-one-text">
                      {intl.formatMessage({ id: 'workinghour.legalHolidays' })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="working-hours__top-right">
                <div className="working-hours__top-right-export" onClick={handleExport}>
                  <SvgCsvGreen />
                  <span>{intl.formatMessage({ id: 'common.csv.export.title' })}</span>
                </div>
                <div className="working-hours__top-right-import" onClick={() => setOpenModalCsv(true)}>
                  <SvgCsv />
                  <span>{intl.formatMessage({ id: 'common.csv.import.title' })}</span>
                </div>
              </div>
            </div>
            <div className="working-hours__month">
              {Array.from({ length: 12 }, (_, monthIndex) => {
                const month = monthIndex + 1;
                return (
                  <div key={month} className="working-hours__month-item">
                    <CalendarCustom
                      year={year}
                      month={month}
                      data={data}
                      isLoading={isLoading}
                      onDayClickProp={(date, type) => {
                        handleDayClick(date, type); // Call handleDayClick for clicked day updates
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="working-hours__button">
              {/* <ButtonWrap width={162} variant="filled" color="black">
                {intl.formatMessage({ id: 'common.cancel' })}
              </ButtonWrap> */}
              <ButtonWrap
                width={162}
                disabled={upsertSchedule.isPending}
                onClick={handleSave}
                variant="filled"
                color="primary"
              >
                {intl.formatMessage({ id: 'common.save' })}
              </ButtonWrap>
            </div>
          </>
        }
        title={intl.formatMessage({ id: 'adminMenu.workingHours' })}
      />
      {openModalCsv && (
        <ModalWrap
          open={openModalCsv}
          cancelText={intl.formatMessage({ id: 'common.cancel' })}
          submitText={intl.formatMessage({ id: 'common.submit' })}
          buttonSubmitProps={{ loading: upsertSchedule.isPending }}
          onSubmit={handleSubmitImportCSV}
          onCancel={handleCloseModalImportCSV}
          type="upsert"
          title={intl.formatMessage({ id: 'common.csv.import.title' })}
          isScrollBody
          body={
            <ImportCSV
              onUploadFile={handleImportCSV}
              type="WORKING_HOURS"
              onConvertDataFile={handleConvertDataFile}
              dataCSVImportError={dataCSVImportError}
            />
          }
          width={1200}
        />
      )}
    </div>
  );
};

export default WorkingHours;
